import { Code, HelpSection, Note, P, Screen, SeeAlso, Steps, Strong, Terms } from '../prose';

/**
 * 管理者の初期設定。
 *
 * 順番そのものが要点。プロジェクト種別を後回しにすると、先に作った
 * プロジェクトの数え方が「種別未設定」のまま残る。
 */
export function AdminSetupArticle() {
  return (
    <>
      <HelpSection heading="登録する順番">
        <P>
          {
            '後ろの項目が前の項目を参照するので、この順に埋めるのが一番早く済みます。すべて画面から登録できます。'
          }
        </P>
        <Steps
          items={[
            {
              title: 'プロジェクト種別を確認する',
              body: (
                <P>
                  <Screen to="/settings/tenant">テナント設定</Screen>
                  {
                    ' にあります。開通時に「受託 / SES」「自社プロダクト開発」「社内業務・保守」の 3 つが入っているので、足りなければ足します。'
                  }
                </P>
              ),
            },
            {
              title: 'プロジェクトを登録する',
              body: (
                <P>
                  <Screen to="/projects">プロジェクト</Screen>
                  {
                    ' で、種別と資産化の可否を決めて登録します。リポジトリの紐付けもここで行います。'
                  }
                </P>
              ),
            },
            {
              title: 'メンバーを招待する',
              body: (
                <P>
                  <Screen to="/members">メンバー</Screen>
                  {
                    ' からメールアドレスと権限を指定して招待します。招待リンクの有効期限は 7 日で、一度使うと無効になります。'
                  }
                </P>
              ),
            },
            {
              title: '単価を入れる',
              body: (
                <P>
                  {'組織の既定単価を '}
                  <Screen to="/settings/tenant">テナント設定</Screen>
                  {' に、人ごとの単価を '}
                  <Screen to="/members">メンバー</Screen>
                  {' に入れます。既定単価だけでも金額の集計は動きます。'}
                </P>
              ),
            },
            {
              title: 'アサインを計画する',
              body: (
                <P>
                  <Screen to="/assignments">アサイン計画</Screen>
                  {
                    ' で、その月に誰をどのプロジェクトに何時間充てるかを登録します。ここが予定、記録された工数が実績です。'
                  }
                </P>
              ),
            },
          ]}
        />
        <Note label="タイムゾーン">
          <P>
            <Screen to="/settings/tenant">テナント設定</Screen>
            {
              ' のタイムゾーンは、記録の「今日」がいつかを決めます。ここが合っていないと、朝の作業が前日の分として記録され、月末の集計がずれます。最初に確認してください。'
            }
          </P>
        </Note>
      </HelpSection>

      <HelpSection heading="プロジェクト種別が決めること">
        <P>
          {
            '種別は単なるラベルではありません。工数の数え方を 2 つ決めます。新しい種別を作るときは、この 2 つを必ず意識してください。'
          }
        </P>
        <Terms
          rows={[
            {
              term: '請求可能の既定',
              value:
                'その種別のプロジェクトに記録された工数を、顧客に請求できるものとして扱うかどうかの既定値です。受託なら有効にします。',
            },
            {
              term: '直接工数として数える',
              value:
                '稼働レポートで直接工数に数えるかどうかです。社内業務や保守のように、売上に直接ひもづかないものは無効にします。',
            },
          ]}
        />
        <P>
          {
            '資産化の可否は種別では決めません。同じ種別でも資産化するプロジェクトとしないプロジェクトが混ざるので、プロジェクトごとの設定にしてあります。'
          }
        </P>
        <Note label="種別を変えると">
          <P>
            {'プロジェクトの種別を後から付け替えると、'}
            <Strong>過去の月の稼働レポートも動きます</Strong>
            {
              '。直接／間接は集計のたびに種別を見て数えるためです。記録済みの請求可能フラグは記録した時点の値のままなので動きません。'
            }
          </P>
        </Note>
      </HelpSection>

      <HelpSection heading="使わなくなった種別">
        <P>
          {
            '使用中の種別は削除できません（プロジェクトの数え方が決まらなくなるため）。今後使わないものは '
          }
          <Code>archived</Code>
          {
            ' にすると、新しいプロジェクトの選択肢から外れます。既存のプロジェクトはそのまま動きます。'
          }
        </P>
      </HelpSection>

      <HelpSection heading="次に見るもの">
        <P>
          {'登録が済んだら、メンバーには '}
          <SeeAlso slug="mcp-setup">MCP をつなぐ</SeeAlso>
          {' を案内してください。集計の見方は '}
          <SeeAlso slug="reading-reports">レポートの読み方</SeeAlso>
          {' にあります。'}
        </P>
      </HelpSection>
    </>
  );
}
